
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import Navbar from '@/components/Navbar';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const PortfolioDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const { data: portfolio, isLoading: isLoadingPortfolio } = useQuery({
    queryKey: ['portfolio', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('portfolios')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) {
        toast.error("Failed to load portfolio");
        throw error;
      }
      
      return data;
    },
    enabled: !!id && !!user,
  });
  
  const { data: portfolioItems, isLoading: isLoadingItems } = useQuery({
    queryKey: ['portfolio-items', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('portfolio_items')
        .select('*')
        .eq('portfolio_id', id)
        .order('created_at', { ascending: false });
      
      if (error) {
        toast.error("Failed to load portfolio items");
        throw error;
      }
      
      return data;
    },
    enabled: !!id && !!user,
  });
  
  if (isLoadingPortfolio || isLoadingItems) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Loading portfolio data...</div>
      </div>
    );
  }
  
  if (!portfolio) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Portfolio not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      
      <div className="container mx-auto pt-24 px-4">
        <Button 
          variant="ghost" 
          className="text-white mb-4"
          onClick={() => navigate('/dashboard')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>
        
        <div className="glass-card rounded-xl p-8">
          <div className="mb-6">
            <h1 className="text-2xl font-bold mb-2">{portfolio.name}</h1>
            {portfolio.description && (
              <p className="text-white/70 mb-4">{portfolio.description}</p>
            )}
          </div>
          
          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4">Portfolio Items</h2>
            
            {portfolioItems && portfolioItems.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Symbol</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Purchase Price</TableHead>
                    <TableHead>Purchase Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {portfolioItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.symbol}</TableCell>
                      <TableCell>{item.asset_type}</TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>{item.purchase_price ? `$${item.purchase_price}` : 'N/A'}</TableCell>
                      <TableCell>{item.purchase_date || 'N/A'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No items in this portfolio yet.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioDetail;
